import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { apiUrls } from '../../../config/api/api-urls.config';
import { ShopItemsResultsResponse } from '../../../models/shop/responses/api-shop-items-results-response.model';
import { ShopItemReward } from '../../../models/shop/shop-item-reward.model';
import { ShopPacksResultsResponse } from '../../../models/shop/responses/api-shop-packs-results-response.model';
import { ShopPackTranslation } from '../../../models/shop/shop-pack-translation.model';
import { ShopPackReward } from '../../../models/shop/shop-pack-reward.model';
import { ShopPackBooster } from '../../../models/shop/shop-pack-booster.model';
import { ShopPacksFiltersRequest } from '../../../models/shop/requests/api-shop-get-packs-request.model';
import { ShopStatisticsResultsResponse } from '../../../models/shop/responses/api-shop-statistics-response.model';

@Injectable({
  providedIn: 'root'
})
export class ApiShopService {

  constructor(private apiService: ApiService) { }

  getItems(): Promise<ShopItemsResultsResponse> {
    return new Promise<ShopItemsResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestGet(apiUrls.shopGetItemsUrl, ShopItemsResultsResponse).then(
        (data: ShopItemsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addItem(name: string, iapId: string, price: number, isPack: boolean, rewards: ShopItemReward[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = {
        name,
        iap_id: iapId,
        price,
        is_pack: isPack,
        rewards: rewards.map(r => ({ currency_type: r.currencyType, currency_quantity: r.currencyQuantity, currency_ticket_cost: r.currencyTicketCost, currency_increase_percent: r.currencyIncreasePercent }))
      };
      this.apiService.launchAdminApiRequestPost(apiUrls.shopAddItemUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editItem(itemId: number, name: string, iapId: string, price: number, isPack: boolean, rewards: ShopItemReward[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = {
        item_id: itemId,
        name,
        iap_id: iapId,
        price,
        is_pack: isPack,
        rewards: rewards.map(r => ({ currency_type: r.currencyType, currency_quantity: r.currencyQuantity, currency_ticket_cost: r.currencyTicketCost, currency_increase_percent: r.currencyIncreasePercent }))
      };
      this.apiService.launchAdminApiRequestPost(apiUrls.shopEditItemUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteItem(itemId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.shopDeleteItemUrl, { item_id: itemId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  getPacks(page: number, resultCount: number, filters: ShopPacksFiltersRequest): Promise<ShopPacksResultsResponse> {
    return new Promise<ShopPacksResultsResponse>((resolve, reject) => {
      const body = { page, result_count: resultCount, filters };
      this.apiService.launchAdminApiRequestPost(apiUrls.shopGetPacksUrl, body, ShopPacksResultsResponse).then(
        (data: ShopPacksResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addPack(name: string, dateStartTimestamp: number, dateEndTimestamp: number, shopItemId: number, translations: ShopPackTranslation[], rewards: ShopPackReward[], boosters: ShopPackBooster[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = {
        name,
        date_start_timestamp: dateStartTimestamp,
        date_end_timestamp: dateEndTimestamp,
        shop_item_id: shopItemId,
        translations: translations.map(t => ({ language_id: t.languageId, title: t.title, description: t.description })),
        rewards: rewards.map(r => ({ shop_item_id: r.shopItemId, currency_type: r.currencyType, currency_quantity: r.currencyQuantity, currency_ticket_cost: r.currencyTicketCost, currency_increase_percent: r.currencyIncreasePercent })),
        boosters: boosters.map(b => ({ booster_type: b.boosterType, booster_multiplier: b.boosterMultiplier, booster_duration: b.boosterDuration }))
      };
      this.apiService.launchAdminApiRequestPost(apiUrls.shopAddPackUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editPack(packId: number, name: string, dateStartTimestamp: number, dateEndTimestamp: number, shopItemId: number, translations: ShopPackTranslation[], rewards: ShopPackReward[], boosters: ShopPackBooster[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = {
        pack_id: packId,
        name,
        date_start_timestamp: dateStartTimestamp,
        date_end_timestamp: dateEndTimestamp,
        shop_item_id: shopItemId,
        translations: translations.map(t => ({ language_id: t.languageId, title: t.title, description: t.description })),
        rewards: rewards.map(r => ({ shop_item_id: r.shopItemId, currency_type: r.currencyType, currency_quantity: r.currencyQuantity, currency_ticket_cost: r.currencyTicketCost, currency_increase_percent: r.currencyIncreasePercent })),
        boosters: boosters.map(b => ({ booster_type: b.boosterType, booster_multiplier: b.boosterMultiplier, booster_duration: b.boosterDuration }))
      };
      this.apiService.launchAdminApiRequestPost(apiUrls.shopEditPackUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deletePack(packId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.shopDeletePackUrl, { pack_id: packId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteNextPacks(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.shopDeleteNextPacksUrl, {}).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  getShopStatistics(startDayTimestamp: number, endDayTimestamp: number): Promise<ShopStatisticsResultsResponse> {
    return new Promise<ShopStatisticsResultsResponse>((resolve, reject) => {
      const body = { start_day_timestamp: startDayTimestamp, end_day_timestamp: endDayTimestamp };
      this.apiService.launchAdminApiRequestPost(apiUrls.shopGetShopStatisticsUrl, body, ShopStatisticsResultsResponse).then(
        (data: ShopStatisticsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addPacksFromFile(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    return this.apiService.launchAdminApiRequestPostFile(apiUrls.shopAddPacksFromFileUrl, formData);
  }
}
